import { useState } from 'react';
import type { JSX } from 'react';
import { IconCheck, IconChevronDown, IconClose, IconSearch } from '../icons/index.js';
import { Avatar } from './Avatar.js';
import { Button } from './Button.js';
import { Input } from './Input.js';
import { SEUIL_RECHERCHE } from './OptionList.js';
import { cn } from '../lib/cn.js';
import { conditionLabel, fieldOperators, joindreValeurs, operatorLabel, searchField, valeursDe } from '../views/field-query.js';
import type { FieldCondition, FieldOperator, QueryField } from '../views/field-query.js';

interface Props {
  id: string;
  fields: QueryField[];
  conditions: FieldCondition[];
  loadedOnly?: boolean;
  hasLegacyFilters?: boolean;
  onSave(condition: FieldCondition): void;
  onRemove(id: string): void;
  onClear(): void;
}
const rowClass = 'flex min-h-8 w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-[0.8125rem] text-ink-700 hover:bg-surface-1 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border';
const fieldKind = (field: QueryField): string => field.kind === 'boolean' ? 'Booléen' : field.personne ? 'Personne' : field.lien ? 'Lien' : field.options !== undefined ? 'Choix' : field.kind === 'number' ? 'Nombre' : field.kind === 'date' || field.kind === 'datetime' ? 'Date' : field.kind === 'lien' ? 'Lien' : 'Texte';

function ChoixDeValeur({ field, multiple, value, onChange }: { field: QueryField; multiple: boolean; value: string; onChange(value: string): void }): JSX.Element {
  const [terme, setTerme] = useState('');
  const choisies = multiple ? valeursDe(value) : [value];
  const toutes = field.options ?? [];
  const proposees = toutes.filter((option) => (!option.archive || choisies.includes(option.value)) && searchField(option.label).includes(searchField(terme)));
  const basculer = (choix: string): void => {
    if (!multiple) return onChange(choix);
    onChange(joindreValeurs(choisies.includes(choix) ? choisies.filter((item) => item !== choix) : [...choisies, choix]));
  };
  return (
    <div className="flex flex-col gap-1.5">
      {toutes.length > SEUIL_RECHERCHE && <Input autoFocus aria-label="Rechercher une valeur" placeholder="Rechercher…" leftIcon={<IconSearch size={14} />} className="h-8 rounded-sm px-2 text-[0.8125rem]" value={terme} onChange={(event) => setTerme(event.target.value)} />}
      <div role="listbox" aria-label="Valeur" aria-multiselectable={multiple} className="max-h-48 overflow-y-auto rounded-sm border border-border-soft p-1">
        {proposees.map((option) => {
          const choisie = choisies.includes(option.value);
          return <button type="button" role="option" aria-selected={choisie} key={option.value} className={cn(rowClass, choisie && 'bg-surface-1 text-ink-900')} onClick={() => basculer(option.value)}>
            {option.avatar && <Avatar size={18} initials={option.label.trim().charAt(0).toUpperCase()} src={option.avatar.src} />}
            <span className={cn('min-w-0 flex-1 truncate', option.archive && 'text-ink-400')}>{option.label}</span>
            {choisie && <IconCheck size={12} />}
          </button>;
        })}
        {proposees.length === 0 && <p className="px-2 py-2 text-xs text-ink-400">Aucun résultat</p>}
      </div>
    </div>
  );
}

export function FieldFilterPopover({ id, fields, conditions, loadedOnly, hasLegacyFilters, onSave, onRemove, onClear }: Props): JSX.Element {
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<FieldCondition | null>(null);
  const selected = fields.find((field) => field.key === draft?.field);
  const visible = fields.filter((field) => searchField(field.label).includes(searchField(search)));
  const noValue = draft?.operator === 'empty' || draft?.operator === 'filled';
  return (
    <div id={id} role="region" aria-label="Filtres" className="absolute right-0 top-[calc(100%+0.4375rem)] z-20 flex max-h-[min(34rem,calc(100dvh-12rem))] w-80 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-md border border-border bg-surface shadow-pop">
      <div className="flex shrink-0 items-center justify-between border-b border-border-soft px-3 py-2">
        <span className="text-[0.8125rem] font-semibold text-ink-900">{draft ? 'Condition' : 'Filtres'}</span>
        {draft && <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>Retour</Button>}
      </div>
      {loadedOnly && <p className="shrink-0 border-b border-border-soft px-3 py-2 text-xs text-ink-500">Filtre sur les fiches chargées</p>}
      {draft && selected ? (
        <form className="flex flex-col gap-2 p-3" onSubmit={(event) => { event.preventDefault(); onSave(draft); setDraft(null); setSearch(''); }}>
          <span className="text-[0.8125rem] font-medium text-ink-900">{selected.label}</span>
          <select aria-label="Condition" value={draft.operator} onChange={(event) => {
            const operator = event.target.value as FieldOperator;
            setDraft({ ...draft, operator, value: draft.operator === 'in' && operator !== 'in' ? valeursDe(draft.value)[0] ?? '' : draft.value });
          }} className="h-8 rounded-sm border border-border bg-surface px-2 text-[0.8125rem] text-ink-700">
            {fieldOperators(selected).map((operator) => <option key={operator} value={operator}>{operatorLabel(operator, selected)}</option>)}
          </select>
          {!noValue && (selected.options !== undefined ? (
            <ChoixDeValeur key={draft.operator === 'in' ? 'multiple' : 'simple'} field={selected} multiple={draft.operator === 'in'} value={draft.value} onChange={(value) => setDraft({ ...draft, value })} />
          ) : <Input autoFocus required aria-label="Valeur" placeholder="Valeur" type={selected.kind === 'number' ? 'number' : selected.kind === 'date' ? 'date' : selected.kind === 'datetime' ? 'datetime-local' : 'text'} step="any" className="h-8 rounded-sm px-2 text-[0.8125rem]" value={draft.value} onChange={(event) => setDraft({ ...draft, value: event.target.value })} />)}
          <Button type="submit" variant="secondary" size="sm" disabled={!noValue && !draft.value.trim()}>Appliquer</Button>
        </form>
      ) : <>
        {conditions.length > 0 && <div className="shrink-0 border-b border-border-soft p-2">
          <p className="px-2 pb-1 text-[0.6875rem] text-ink-400">Toutes les conditions</p>
          {conditions.map((condition) => {
            const field = fields.find((candidate) => candidate.key === condition.field);
            if (!field) return null;
            return <div key={condition.id} className="flex items-center gap-1">
              <button type="button" className={cn(rowClass, 'min-w-0 flex-1')} onClick={() => setDraft(condition)}><span className="truncate">{conditionLabel(condition, field)}</span></button>
              <Button variant="ghost" size="sm" className="w-7 shrink-0 px-0" aria-label={`Retirer ${field.label}`} onClick={() => onRemove(condition.id)}><IconClose size={12} /></Button>
            </div>;
          })}
        </div>}
        <div className="shrink-0 p-2"><Input autoFocus aria-label="Rechercher un champ à filtrer" placeholder="Rechercher un champ…" leftIcon={<IconSearch size={14} />} className="h-8 rounded-sm" value={search} onChange={(event) => setSearch(event.target.value)} /></div>
        <div className="min-h-0 overflow-y-auto px-2 pb-2">
          {visible.map((field) => <button type="button" key={field.key} className={rowClass} onClick={() => setDraft({ id: `~field:${Date.now()}`, field: field.key, operator: fieldOperators(field)[0]!, value: '' })}>
            <span className="min-w-0 flex-1 truncate">{field.label}</span><span className="text-[0.6875rem] text-ink-400">{fieldKind(field)}</span><IconChevronDown size={10} className="-rotate-90" />
          </button>)}
          {visible.length === 0 && <p className="px-2 py-3 text-xs text-ink-400">Aucun champ</p>}
        </div>
        <div className="shrink-0 border-t border-border-soft p-2"><Button variant="ghost" size="sm" className="w-full justify-start px-2" disabled={!conditions.length && !hasLegacyFilters} onClick={onClear}>Réinitialiser</Button></div>
      </>}
    </div>
  );
}
